// External packages
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextStyle from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import TextAlign from '@tiptap/extension-text-align';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Underline from '@tiptap/extension-underline';
import Strike from '@tiptap/extension-strike';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import ListItem from '@tiptap/extension-list-item';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Heading from '@tiptap/extension-heading';
import Link from '@tiptap/extension-link';
import Blockquote from '@tiptap/extension-blockquote';
import * as React from 'react';
import * as ReactHookForm from 'react-hook-form';
import * as ReactAria from 'react-aria-components';

// Components
import { Button, ButtonIcon } from 'components/Button';
import * as Forms from 'components/Forms';
import { Icon } from 'components/Icons';

// Utils
import { twMerge } from 'tailwind-merge';

interface TiptapEditorProps {
  content?: string;
  onChange?: (content: string) => void;
  className?: string;
  colorScheme?: 'white' | 'gray';
  isInvalid?: boolean;
  disabled?: boolean;
}

export type TiptapEditorFieldProps = {
  name: string;
  label?: string;
  labelProps?: Forms.LabelProps;
  editorProps?: Omit<TiptapEditorProps, 'content' | 'onChange'>;
  subLabelProps?: Forms.SubLabelProps;
} & ReactAria.TextFieldProps;

const MenuBar: React.FC<{ editor: any }> = ({ editor }) => {
  const [showColorPicker, setShowColorPicker] = React.useState(false);
  const [showLinkDialog, setShowLinkDialog] = React.useState(false);
  const [linkUrl, setLinkUrl] = React.useState('');
  const linkDialogRef = React.useRef<HTMLDivElement>(null);
  const colorPickerRef = React.useRef<HTMLDivElement>(null);

  if (!editor) {
    return null;
  }

  // Close modals when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        linkDialogRef.current &&
        !linkDialogRef.current.contains(event.target as Node)
      ) {
        setShowLinkDialog(false);
        setLinkUrl('');
      }
      if (
        colorPickerRef.current &&
        !colorPickerRef.current.contains(event.target as Node)
      ) {
        setShowColorPicker(false);
      }
    };

    if (showLinkDialog || showColorPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showLinkDialog, showColorPicker]);

  const addLink = () => {
    if (linkUrl) {
      editor.chain().focus().setLink({ href: linkUrl }).run();
      setLinkUrl('');
      setShowLinkDialog(false);
    }
  };

  const removeLink = () => {
    editor.chain().focus().unsetLink().run();
  };

  const setColor = (color: string) => {
    editor.chain().focus().setColor(color).run();
    setShowColorPicker(false);
  };

  return (
    <div className="border-b border-gray-200 p-2 flex flex-wrap gap-1">
      {/* Undo */}
      <ButtonIcon
        colorScheme="secondary"
        variant="ghost"
        size="sm"
        onPress={() => editor.chain().focus().undo().run()}
        isDisabled={!editor.can().undo()}
      >
        <Icon name="undo" className="size-4" />
      </ButtonIcon>

      {/* Redo */}
      <ButtonIcon
        colorScheme="secondary"
        variant="ghost"
        size="sm"
        onPress={() => editor.chain().focus().redo().run()}
        isDisabled={!editor.can().redo()}
      >
        <Icon name="redo" className="size-4" />
      </ButtonIcon>

      <div className="w-px h-6 bg-gray-300 mx-1" />

      {/* Bold */}
      <ButtonIcon
        colorScheme={editor.isActive('bold') ? 'primary' : 'secondary'}
        variant="ghost"
        size="sm"
        onPress={() => editor.chain().focus().toggleBold().run()}
      >
        <Icon name="bold" className="size-4" />
      </ButtonIcon>

      {/* Italic */}
      <ButtonIcon
        colorScheme={editor.isActive('italic') ? 'primary' : 'secondary'}
        variant="ghost"
        size="sm"
        onPress={() => editor.chain().focus().toggleItalic().run()}
      >
        <Icon name="italic" className="size-4" />
      </ButtonIcon>

      {/* Underline */}
      <ButtonIcon
        colorScheme={editor.isActive('underline') ? 'primary' : 'secondary'}
        variant="ghost"
        size="sm"
        onPress={() => editor.chain().focus().toggleUnderline().run()}
      >
        <Icon name="underline" className="size-4" />
      </ButtonIcon>

      {/* Strike */}
      <ButtonIcon
        colorScheme={editor.isActive('strike') ? 'primary' : 'secondary'}
        variant="ghost"
        size="sm"
        onPress={() => editor.chain().focus().toggleStrike().run()}
      >
        <Icon name="strikethrough" className="size-4" />
      </ButtonIcon>

      <div className="w-px h-6 bg-gray-300 mx-1" />

      {/* Link */}
      <div className="relative">
        <ButtonIcon
          colorScheme={editor.isActive('link') ? 'primary' : 'secondary'}
          variant="ghost"
          size="sm"
          onPress={() => {
            if (editor.isActive('link')) {
              removeLink();
            } else {
              setShowLinkDialog(!showLinkDialog);
            }
          }}
        >
          <Icon name="link" className="size-4" />
        </ButtonIcon>

        {showLinkDialog && (
          <div
            ref={linkDialogRef}
            className="absolute top-10 left-0 z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-3 min-w-64"
          >
            <div className="flex gap-2">
              <Forms.Input
                type="url"
                placeholder="Enter URL..."
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    addLink();
                  } else if (e.key === 'Escape') {
                    setShowLinkDialog(false);
                    setLinkUrl('');
                  }
                }}
                className="h-auto w-40"
                autoFocus
              />
              <Button size="sm" onPress={addLink}>
                Add
              </Button>
              <Button
                size="sm"
                variant="outline"
                onPress={() => {
                  setShowLinkDialog(false);
                  setLinkUrl('');
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Blockquote */}
      <ButtonIcon
        colorScheme={editor.isActive('blockquote') ? 'primary' : 'secondary'}
        variant="ghost"
        size="sm"
        onPress={() => editor.chain().focus().toggleBlockquote().run()}
      >
        <Icon name="blockquote" className="size-4" />
      </ButtonIcon>

      {/* Color Picker */}
      <div className="relative">
        <ButtonIcon
          colorScheme="secondary"
          variant="ghost"
          size="sm"
          onPress={() => setShowColorPicker(!showColorPicker)}
          style={{
            color: editor.isActive('textStyle')
              ? editor.getAttributes('textStyle').color || undefined
              : undefined,
          }}
        >
          <Icon name="palette" className="size-4" />
        </ButtonIcon>

        {showColorPicker && (
          <div
            ref={colorPickerRef}
            className="absolute w-max top-10 left-0 z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-3"
          >
            <div className="grid grid-cols-6 gap-2">
              {[
                '#000000',
                '#374151',
                '#6B7280',
                '#EF4444',
                '#F97316',
                '#EAB308',
                '#22C55E',
                '#10B981',
                '#06B6D4',
                '#3B82F6',
                '#6366F1',
                '#8B5CF6',
                '#EC4899',
                '#F43F5E',
                '#64748B',
                '#78716C',
                '#DC2626',
                '#EA580C',
                '#CA8A04',
                '#16A34A',
                '#059669',
                '#0891B2',
                '#2563EB',
                '#4F46E5',
                '#7C3AED',
                '#C026D3',
                '#BE185D',
                '#E11D48',
              ].map((color) => (
                <button
                  key={color}
                  onClick={() => setColor(color)}
                  className="w-6 h-6 rounded border border-gray-300 hover:scale-110 transition-transform"
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
            </div>
            <div className="mt-2 pt-2 border-t border-gray-200">
              <button
                onClick={() => editor.chain().focus().unsetColor().run()}
                className="w-full px-2 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
              >
                Remove Color
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="w-px h-6 bg-gray-300 mx-1" />

      {/* Bullet List */}
      <ButtonIcon
        colorScheme={editor.isActive('bulletList') ? 'primary' : 'secondary'}
        variant="ghost"
        size="sm"
        onPress={() => editor.chain().focus().toggleBulletList().run()}
      >
        <Icon name="bulleted-list" className="size-4" />
      </ButtonIcon>

      {/* Ordered List */}
      <ButtonIcon
        colorScheme={editor.isActive('orderedList') ? 'primary' : 'secondary'}
        variant="ghost"
        size="sm"
        onPress={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <Icon name="numbered-list" className="size-4" />
      </ButtonIcon>

      {/* Task List (Checklist) */}
      <ButtonIcon
        colorScheme={editor.isActive('taskList') ? 'primary' : 'secondary'}
        variant="ghost"
        size="sm"
        onPress={() => editor.chain().focus().toggleTaskList().run()}
      >
        <Icon name="checks-list" className="size-4" />
      </ButtonIcon>

      <div className="w-px h-6 bg-gray-300 mx-1" />

      {/* Text Alignment */}
      <ButtonIcon
        colorScheme={
          editor.isActive({ textAlign: 'left' }) ? 'primary' : 'secondary'
        }
        variant="ghost"
        size="sm"
        onPress={() => editor.chain().focus().setTextAlign('left').run()}
      >
        <Icon name="align-left" className="size-4" />
      </ButtonIcon>

      <ButtonIcon
        colorScheme={
          editor.isActive({ textAlign: 'center' }) ? 'primary' : 'secondary'
        }
        variant="ghost"
        size="sm"
        onPress={() => editor.chain().focus().setTextAlign('center').run()}
      >
        <Icon name="align-center" className="size-4" />
      </ButtonIcon>

      <ButtonIcon
        colorScheme={
          editor.isActive({ textAlign: 'right' }) ? 'primary' : 'secondary'
        }
        variant="ghost"
        size="sm"
        onPress={() => editor.chain().focus().setTextAlign('right').run()}
      >
        <Icon name="align-right" className="size-4" />
      </ButtonIcon>

      <ButtonIcon
        colorScheme={
          editor.isActive({ textAlign: 'justify' }) ? 'primary' : 'secondary'
        }
        variant="ghost"
        size="sm"
        onPress={() => editor.chain().focus().setTextAlign('justify').run()}
      >
        <Icon name="align-justify" className="size-4" />
      </ButtonIcon>

      <div className="w-px h-6 bg-gray-300 mx-1" />

      {/* Headings */}
      <ButtonIcon
        colorScheme={
          editor.isActive('heading', { level: 1 }) ? 'primary' : 'secondary'
        }
        variant="ghost"
        size="sm"
        onPress={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
      >
        <Icon name="h1" className="size-4" />
      </ButtonIcon>

      <ButtonIcon
        colorScheme={
          editor.isActive('heading', { level: 2 }) ? 'primary' : 'secondary'
        }
        variant="ghost"
        size="sm"
        onPress={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        <Icon name="h2" className="size-4" />
      </ButtonIcon>

      <ButtonIcon
        colorScheme={
          editor.isActive('heading', { level: 3 }) ? 'primary' : 'secondary'
        }
        variant="ghost"
        size="sm"
        onPress={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      >
        <Icon name="h3" className="size-4" />
      </ButtonIcon>

      <ButtonIcon
        colorScheme={
          editor.isActive('heading', { level: 4 }) ? 'primary' : 'secondary'
        }
        variant="ghost"
        size="sm"
        onPress={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}
      >
        <Icon name="h4" className="size-4" />
      </ButtonIcon>

      <ButtonIcon
        colorScheme={
          editor.isActive('heading', { level: 5 }) ? 'primary' : 'secondary'
        }
        variant="ghost"
        size="sm"
        onPress={() => editor.chain().focus().toggleHeading({ level: 5 }).run()}
      >
        <Icon name="h5" className="size-4" />
      </ButtonIcon>

      <ButtonIcon
        colorScheme={
          editor.isActive('heading', { level: 6 }) ? 'primary' : 'secondary'
        }
        variant="ghost"
        size="sm"
        onPress={() => editor.chain().focus().toggleHeading({ level: 6 }).run()}
      >
        <Icon name="h6" className="size-4" />
      </ButtonIcon>
    </div>
  );
};

export const TiptapEditor: React.FC<TiptapEditorProps> = ({
  content = '',
  onChange,
  className,
  colorScheme = 'gray',
  isInvalid = false,
  disabled = false,
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: false,
        orderedList: false,
        listItem: false,
        bold: false,
        italic: false,
        strike: false,
        heading: false,
        blockquote: false,
      }),
      Bold,
      Italic,
      Underline,
      Strike,
      BulletList.configure({
        HTMLAttributes: {
          class: 'tiptap-bullet-list',
        },
      }),
      OrderedList.configure({
        HTMLAttributes: {
          class: 'tiptap-ordered-list',
        },
      }),
      ListItem,
      TaskList.configure({
        HTMLAttributes: {
          class: 'tiptap-task-list',
        },
      }),
      TaskItem.configure({
        HTMLAttributes: {
          class: 'tiptap-task-item',
        },
        nested: true,
      }),
      Heading.configure({
        levels: [1, 2, 3, 4, 5, 6],
        HTMLAttributes: {
          class: 'tiptap-heading',
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'tiptap-link',
        },
      }),
      Blockquote.configure({
        HTMLAttributes: {
          class: 'tiptap-blockquote',
        },
      }),
      TextStyle,
      Color,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
  });

  React.useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content);
    }
  }, [editor, content]);

  const editorClasses = twMerge(
    'min-h-45 w-full rounded-lg border border-gray-200 transition-colors',
    colorScheme === 'white' ? 'bg-white' : 'bg-gray-75',
    isInvalid && 'border-red-600',
    className
  );

  return (
    <div className={editorClasses}>
      {!disabled && <MenuBar editor={editor} />}
      <EditorContent
        editor={editor}
        className={twMerge(
          'prose prose-sm max-w-none p-4 focus-within:outline-none',
          // ProseMirror base styles
          '[&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-32',
          // Placeholder styles for empty editor
          '[&_.ProseMirror_p.is-editor-empty:first-child::before]:text-gray-500',
          '[&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left',
          '[&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none',
          '[&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0',
          '[&_.ProseMirror_.is-empty::before]:text-gray-500',
          '[&_.ProseMirror_.is-empty::before]:float-left',
          '[&_.ProseMirror_.is-empty::before]:pointer-events-none',
          '[&_.ProseMirror_.is-empty::before]:h-0',
          // List styles
          '[&_.tiptap-bullet-list]:list-disc [&_.tiptap-bullet-list]:ml-6',
          '[&_.tiptap-ordered-list]:list-decimal [&_.tiptap-ordered-list]:ml-6',
          '[&_ul]:list-disc [&_ul]:ml-6',
          '[&_ol]:list-decimal [&_ol]:ml-6',
          '[&_li]:mb-1',
          '[&_.tiptap-bullet-list_li]:mb-1',
          '[&_.tiptap-ordered-list_li]:mb-1',
          // Text alignment styles
          '[&_.ProseMirror_[style*="text-align:_left"]]:text-left',
          '[&_.ProseMirror_[style*="text-align:_center"]]:text-center',
          '[&_.ProseMirror_[style*="text-align:_right"]]:text-right',
          '[&_.ProseMirror_[style*="text-align:_justify"]]:text-justify',
          // Heading styles
          '[&_.tiptap-heading]:font-semibold [&_.tiptap-heading]:mt-4 [&_.tiptap-heading]:mb-2',
          '[&_.ProseMirror_h1]:text-4xl [&_.ProseMirror_h1]:leading-10 [&_.ProseMirror_h1]:font-bold [&_.ProseMirror_h1]:mt-6 [&_.ProseMirror_h1]:mb-4',
          '[&_.ProseMirror_h2]:text-3xl [&_.ProseMirror_h2]:leading-9 [&_.ProseMirror_h2]:font-semibold [&_.ProseMirror_h2]:mt-5 [&_.ProseMirror_h2]:mb-3',
          '[&_.ProseMirror_h3]:text-2xl [&_.ProseMirror_h3]:leading-8 [&_.ProseMirror_h3]:font-semibold [&_.ProseMirror_h3]:mt-4 [&_.ProseMirror_h3]:mb-3',
          '[&_.ProseMirror_h4]:text-xl [&_.ProseMirror_h4]:leading-7 [&_.ProseMirror_h4]:font-semibold [&_.ProseMirror_h4]:mt-4 [&_.ProseMirror_h4]:mb-2',
          '[&_.ProseMirror_h5]:text-lg [&_.ProseMirror_h5]:leading-6 [&_.ProseMirror_h5]:font-semibold [&_.ProseMirror_h5]:mt-3 [&_.ProseMirror_h5]:mb-2',
          '[&_.ProseMirror_h6]:text-base [&_.ProseMirror_h6]:leading-5 [&_.ProseMirror_h6]:font-semibold [&_.ProseMirror_h6]:mt-3 [&_.ProseMirror_h6]:mb-2',
          // Remove top margin from first element
          '[&_.ProseMirror_>*:first-child]:mt-0',
          // Link styles
          '[&_.tiptap-link]:text-blue-600 [&_.tiptap-link]:underline [&_.tiptap-link]:cursor-pointer hover:[&_.tiptap-link]:text-blue-800',
          // Blockquote styles
          '[&_.tiptap-blockquote]:border-l-4 [&_.tiptap-blockquote]:border-gray-300 [&_.tiptap-blockquote]:pl-4 [&_.tiptap-blockquote]:py-2 [&_.tiptap-blockquote]:my-4 [&_.tiptap-blockquote]:italic [&_.tiptap-blockquote]:text-gray-700 [&_.tiptap-blockquote]:bg-gray-50',
          // Task list styles (based on official Tiptap examples)
          '[&_ul[data-type="taskList"]]:list-none [&_ul[data-type="taskList"]]:ml-0 [&_ul[data-type="taskList"]]:p-0',
          '[&_ul[data-type="taskList"]_li]:flex [&_ul[data-type="taskList"]_li]:items-start [&_ul[data-type="taskList"]_li]:mb-1',
          '[&_ul[data-type="taskList"]_li>label]:flex-none [&_ul[data-type="taskList"]_li>label]:mr-2 [&_ul[data-type="taskList"]_li>label]:select-none',
          '[&_ul[data-type="taskList"]_li>div]:flex-1',
          '[&_ul[data-type="taskList"]_input[type="checkbox"]]:cursor-pointer',
          '[&_ul[data-type="taskList"]]:!ml-2'
        )}
      />
    </div>
  );
};

export const TiptapEditorField: React.FC<TiptapEditorFieldProps> = ({
  name,
  label,
  labelProps,
  editorProps,
  subLabelProps,
  isInvalid,
  defaultValue,
  className,
  ...props
}) => {
  const { field, fieldState } = ReactHookForm.useController<
    { __name__: string },
    '__name__'
  >({ name: name as '__name__', defaultValue });

  return (
    <Forms.TextField
      {...props}
      id={name}
      isInvalid={Boolean(fieldState.error) ?? isInvalid}
      className={twMerge('group', className as string)}
    >
      {label && (
        <Forms.Label {...labelProps} htmlFor={name}>
          {label}
        </Forms.Label>
      )}
      <TiptapEditor
        {...editorProps}
        content={field.value ?? ''}
        onChange={field.onChange}
        isInvalid={Boolean(fieldState.error) ?? isInvalid}
      />
      {fieldState.error && (
        <Forms.SubLabel
          {...subLabelProps}
          role="alert"
          slot="errorMessage"
          colorScheme="error"
        >
          {fieldState.error.message}
        </Forms.SubLabel>
      )}
    </Forms.TextField>
  );
};

export default TiptapEditor;
